import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";

import apiClient from "../../axiosInstance";
import showAlert from "../../components/Common/ShowAlert";
import TanstackTable from "../../components/Common/TanstackTable";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import logo from "../../assets/images/logo.png";

const getTodayDate = () => new Date().toISOString().split("T")[0];

const Charges = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Charges", link: "#" },
  ]);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({ from_date: "", to_date: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Separate variables for options
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [filteredPaymentAccounts, setFilteredPaymentAccounts] = useState([]);

  // Form data for modal inputs
  const [formData, setFormData] = useState({
    chargeId: null,
    amount: null,
    paymentMethodId: "",
    paymentAccountId: "",
    paymentReference: "",
    paymentDate: getTodayDate(),
  });

  const [editFormData, setEditFormData] = useState({
    chargeId: null,
    amount: "",
    dueDate: "",
  });

  const fetchCharges = async () => {
    try {
      const result = await apiClient.get("/payments/charges");
      setData(result.data.response || []);
    } catch (err) {
      console.error("Error fetching charges:", err);
    }
  };

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const methodsResult = await apiClient.get("/payment/payment-methods");
        const methodsData =
          methodsResult.data.response || methodsResult.data || [];
        setPaymentMethods(
          methodsData.map((m) => ({ value: m.id, label: m.name }))
        );
      } catch (err) {
        console.error("Error fetching methods:", err);
      }
    };

    const fetchPaymentAccounts = async () => {
      try {
        const accountsResult = await apiClient.get("/payment/payment-accounts");
        const accountsData =
          accountsResult.data.response || accountsResult.data || [];
        setPaymentAccounts(
          accountsData.map((a) => ({
            value: a.id,
            label: a.account,
            methodId: a.method_id, // Assuming accounts are linked to methods
          }))
        );
      } catch (err) {
        console.error("Error fetching accounts:", err);
      }
    };

    fetchCharges();
    fetchPaymentMethods();
    fetchPaymentAccounts();
  }, []);

  const getFilteredCharges = (rows, activeFilters) => {
    const fromDate = activeFilters.from_date
      ? new Date(`${activeFilters.from_date}T00:00:00`)
      : null;
    const toDate = activeFilters.to_date
      ? new Date(`${activeFilters.to_date}T23:59:59`)
      : null;

    return rows.filter((row) => {
      const rowDateValue = row.registration_date || row.due_date;
      if (!rowDateValue) {
        return !fromDate && !toDate;
      }

      const rowDate = new Date(rowDateValue);
      if (Number.isNaN(rowDate.getTime())) {
        return !fromDate && !toDate;
      }

      if (fromDate && rowDate < fromDate) {
        return false;
      }
      if (toDate && rowDate > toDate) {
        return false;
      }
      return true;
    });
  };

  useEffect(() => {
    setFilteredData(getFilteredCharges(data, filters));
  }, [data]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilters = async () => {
    if (
      filters.from_date &&
      filters.to_date &&
      filters.from_date > filters.to_date
    ) {
      await showAlert({
        title: "Charges",
        text: "From date cannot be greater than To date",
        icon: "warning",
      });
      return;
    }

    setFilteredData(getFilteredCharges(data, filters));
  };

  const resetFilters = () => {
    const resetState = { from_date: "", to_date: "" };
    setFilters(resetState);
    setFilteredData(data);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "";
    }
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) {
      return "";
    }
    return parsed.toLocaleDateString();
  };

  const handlePrint = async () => {
    if (!filteredData.length) {
      await showAlert({
        title: "Charges",
        text: "No data available to print",
        icon: "warning",
      });
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const reportRows = filteredData
      .map(
        (row, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(`${row.make_name || ""} ${row.model_name || ""} (${row.license_plate || ""})`)}</td>
            <td>${escapeHtml(`${row.full_name || ""} (${row.phone_number || ""})`)}</td>
            <td>${escapeHtml(formatDate(row.registration_date))}</td>
            <td>${escapeHtml(row.purpose || "")}</td>
            <td>${escapeHtml(row.amount || "")}</td>
            <td>${escapeHtml(row.status || "")}</td>
          </tr>
        `
      )
      .join("");

    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      await showAlert({
        title: "Charges",
        text: "Unable to open print window. Please allow popups and try again.",
        icon: "error",
      });
      return;
    }

    const generatedAt = new Date().toLocaleString();

    const printContent = `
      <html>
        <head>
          <title>Charges Report</title>
          <style>
            @page { margin: 15mm 14mm; }
            body { font-family: "Segoe UI", Arial, sans-serif; margin: 18px; color: #1f2937; }
            .header { text-align: center; border-bottom: 2px solid #6b7280; padding-bottom: 12px; margin-bottom: 12px; }
            .logo { width: 100mm; height: 50mm; object-fit: contain; padding-top: 10mm; margin-bottom: 6px; margin-top: -50px; }
            h2 { margin: 0 0 6px; font-weight: 700; }
            .meta { margin-bottom: 14px; font-size: 12px; color: #374151; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 18px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #6b7280; padding: 7px 8px; text-align: left; }
            th { background: #e5e7eb; color: #111827; }
            tbody tr:nth-child(even) { background: #f9fafb; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${logo}" alt="Daryeel Logo" class="logo" />
            <h2>Charges Report</h2>
          </div>
          <div class="meta">
            <div><strong>From Date:</strong> ${escapeHtml(filters.from_date || "-")}</div>
            <div><strong>To Date:</strong> ${escapeHtml(filters.to_date || "-")}</div>
            <div><strong>Generated:</strong> ${escapeHtml(generatedAt)}</div>
            <div><strong>Total Records:</strong> ${filteredData.length}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>No</th>
                <th>Vehicle</th>
                <th>Owner</th>
                <th>Reg. Date</th>
                <th>Purpose</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${reportRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const columns = [
    {
      id: "index",
      header: "No",
      cell: ({ row }) => row.index + 1,
    },
    {
      accessorFn: (row) =>
        `${row.make_name || ""} ${row.model_name || ""} (${
          row.license_plate || ""
        })`,
      id: "vehicle",
      header: "Vehicle",
      cell: ({ row }) => {
        const { make_name, model_name, license_plate } = row.original;
        return (
          <>
            {make_name} {model_name} ({license_plate})
          </>
        );
      },
    },
    {
      accessorFn: (row) =>
        `${row.full_name || ""} (${row.phone_number || ""})`,
      id: "owner",
      header: "Owner",
      cell: ({ row }) => {
        const { full_name, phone_number } = row.original;
        return `${full_name} (${phone_number || ""})`;
      },
    },
    {
      accessorKey: "registration_date",
      header: "Registration Date",
      cell: ({ row }) =>
        row.original.registration_date
          ? new Date(row.original.registration_date).toLocaleDateString()
          : "",
    },
    {
      accessorKey: "purpose",
      header: "Purpose",
    },
    {
      accessorKey: "amount",
      header: "Amount",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <span
            className={`badge bg-${status === "Unpaid" ? "danger" : "success"}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const status = row.original.status;
        if (status === "Unpaid") {
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  setFormData({
                    chargeId: row.original.id,
                    amount: row.original.amount,
                    paymentMethodId: "",
                    paymentAccountId: "",
                    paymentReference: "",
                    paymentDate: getTodayDate(),
                  });
                  setModalOpen(true);
                }}
              >
                Pay
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => handleDeleteCharge(row.original.id)}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => handleEditClick(row.original)}
              >
                <i className="ri-file-edit-line"></i>
              </button>
            </div>
          );
        }
        return (
          <div className="d-flex gap-2 align-items-center">
            <span className="text-muted">Paid</span>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => handleEditClick(row.original)}
            >
              <i className="ri-file-edit-line"></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => handleDeleteCharge(row.original.id)}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        );
      },
    },
  ];

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    setFilteredPaymentAccounts([]);
  };

  const toggleEditModal = () => {
    setEditModalOpen(!editModalOpen);
    if (editModalOpen) {
      setEditFormData({
        chargeId: null,
        amount: "",
        dueDate: "",
      });
    }
  };

  const handleEditClick = (charge) => {
    setEditFormData({
      chargeId: charge.id,
      amount: charge.amount ?? "",
      dueDate: charge.due_date ? String(charge.due_date).split("T")[0] : "",
    });
    setEditModalOpen(true);
  };

  const handlePaymentMethodChange = (selected) => {
    const methodId = selected?.value || "";
    setFormData((prevData) => ({
      ...prevData,
      paymentMethodId: methodId,
      paymentAccountId: "", // Reset payment account when method changes
    }));

    // Filter payment accounts based on selected method
    const filteredAccounts = paymentAccounts.filter(
      (account) => account.methodId === methodId
    );
    setFilteredPaymentAccounts(filteredAccounts);
  };

  const handlePayment = async () => {
    try {
      const response = await apiClient.post("/payments/payments", formData);
      await showAlert({
        title: "Payment",
        text: response.data.message,
        icon: response.data.status,
        goBack: false,
        timer: 5000,
        confirm: true,
        link: `https://daryeelinspection.com/api/payments/invoice/${formData.chargeId}`,
      });
      if (response.data.status === "success") {
        setFormData({
          chargeId: null,
          amount: null,
          paymentMethodId: "",
          paymentAccountId: "",
          paymentReference: "",
          paymentDate: getTodayDate(),
        });
        setModalOpen(false);
        fetchCharges();
      }
    } catch (err) {
      console.error("Error while paying charge:", err);
    }
  };

  const handleDeleteCharge = async (chargeId) => {
    const confirmResult = await Swal.fire({
      title: "Delete Charge?",
      text: "Are you sure you want to delete this charge?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      const response = await apiClient.delete(`/payments/charges/${chargeId}`);

      if (response.data.status === "success") {
        setData((prevData) => prevData.filter((charge) => charge.id !== chargeId));
        await Swal.fire({
          title: "Deleted",
          text: response.data.message || "Charge deleted successfully",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        return;
      }

      await Swal.fire({
        title: "Delete Failed",
        text: response.data.message || "Failed to delete charge",
        icon: "error",
      });
    } catch (error) {
      console.error("Error deleting charge:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to delete charge";
      await Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
      });
    }
  };

  const handleUpdateCharge = async () => {
    try {
      const response = await apiClient.put(
        `/payments/charges/${editFormData.chargeId}`,
        editFormData
      );

      if (response.data.status === "success") {
        await Swal.fire({
          title: "Updated",
          text: response.data.message || "Charge updated successfully",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        setEditModalOpen(false);
        setEditFormData({
          chargeId: null,
          amount: "",
          dueDate: "",
        });
        fetchCharges();
        return;
      }

      await Swal.fire({
        title: "Update Failed",
        text: response.data.message || "Failed to update charge",
        icon: "error",
      });
    } catch (error) {
      console.error("Error updating charge:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to update charge";
      await Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
      });
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Charges" breadcrumbItems={breadcrumbItems} />
        <Card>
          <CardBody>
            <Row className="g-3 align-items-end">
              <Col md="4">
                <Label className="form-label fw-semibold">From Date</Label>
                <Input
                  type="date"
                  name="from_date"
                  value={filters.from_date}
                  onChange={handleFilterChange}
                />
              </Col>
              <Col md="4">
                <Label className="form-label fw-semibold">To Date</Label>
                <Input
                  type="date"
                  name="to_date"
                  value={filters.to_date}
                  onChange={handleFilterChange}
                />
              </Col>
              <Col md="4">
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-grow-1"
                    onClick={applyFilters}
                  >
                    Filter
                  </button>
                  <button
                    type="button"
                    className="btn btn-light border flex-grow-1"
                    onClick={resetFilters}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-success flex-grow-1"
                    onClick={handlePrint}
                  >
                    Print
                  </button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <TanstackTable data={filteredData} columns={columns} />
          </CardBody>
        </Card>

        <Modal
          isOpen={modalOpen}
          toggle={toggleModal}
          centered={true}
          backdrop="static"
        >
          <ModalHeader toggle={toggleModal}>Pay Charge</ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label>Payment Method</Label>
              <Select
                value={paymentMethods.find(
                  (m) => m.value === formData.paymentMethodId
                )}
                onChange={handlePaymentMethodChange}
                options={paymentMethods}
                classNamePrefix="select2-selection form-control"
              />
            </div>
            <div className="mb-3">
              <Label>Payment Account</Label>
              <Select
                value={filteredPaymentAccounts.find(
                  (a) => a.value === formData.paymentAccountId
                )}
                onChange={(selected) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    paymentAccountId: selected?.value || "",
                  }))
                }
                options={filteredPaymentAccounts}
                classNamePrefix="select2-selection form-control"
              />
            </div>
            <div className="mb-3">
              <Label>Reference</Label>
              <input
                type="text"
                className="form-control"
                placeholder="Payment Reference"
                value={formData.paymentReference}
                onChange={(e) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    paymentReference: e.target.value,
                  }))
                }
              />
            </div>
            <div className="mb-3">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={formData.paymentDate || ""}
                onChange={(e) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    paymentDate: e.target.value,
                  }))
                }
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-secondary" onClick={toggleModal}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePayment}
              disabled={
                !formData.paymentMethodId ||
                !formData.paymentAccountId ||
                !formData.paymentDate
              }
            >
              Pay
            </button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={editModalOpen}
          toggle={toggleEditModal}
          centered={true}
          backdrop="static"
        >
          <ModalHeader toggle={toggleEditModal}>Edit Charge</ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label>Amount</Label>
              <input
                type="number"
                className="form-control"
                value={editFormData.amount}
                onChange={(e) =>
                  setEditFormData((prevData) => ({
                    ...prevData,
                    amount: e.target.value,
                  }))
                }
              />
            </div>
            <div className="mb-3">
              <Label>Due Date</Label>
              <input
                type="date"
                className="form-control"
                value={editFormData.dueDate || ""}
                onChange={(e) =>
                  setEditFormData((prevData) => ({
                    ...prevData,
                    dueDate: e.target.value,
                  }))
                }
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-secondary" onClick={toggleEditModal}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleUpdateCharge}
              disabled={!editFormData.amount}
            >
              Update
            </button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default Charges;
